<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import evolutionAPI from 'dashboard/api/evolution';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();

const instances = ref([]);
const isLoading = ref(false);
const connectingInstance = ref('');
const disconnectingInstance = ref('');
const instanceToDisconnect = ref('');
const selectedInstance = ref('');
const qrCode = ref('');
const disconnectDialog = ref(null);

const hasNoInstances = computed(
  () => !isLoading.value && instances.value.length === 0
);

const statusLabel = status => {
  const normalizedStatus = String(status || 'unknown').toLowerCase();
  if (normalizedStatus === 'open') return t('ATN_EVOLUTION.STATUS.OPEN');
  if (normalizedStatus === 'connecting') {
    return t('ATN_EVOLUTION.STATUS.CONNECTING');
  }
  if (normalizedStatus === 'close') return t('ATN_EVOLUTION.STATUS.CLOSE');
  return t('ATN_EVOLUTION.STATUS.UNKNOWN');
};

const statusClass = status => {
  const normalizedStatus = String(status).toLowerCase();
  if (normalizedStatus === 'open') return 'bg-n-teal-3 text-n-teal-11';
  if (normalizedStatus === 'connecting') {
    return 'bg-n-amber-3 text-n-amber-11';
  }
  return 'bg-n-ruby-3 text-n-ruby-11';
};

const loadInstances = async ({ silent = false } = {}) => {
  if (!silent) isLoading.value = true;
  try {
    const { data } = await evolutionAPI.listInstances();
    instances.value = data.instances || [];
  } catch (error) {
    useAlert(error.response?.data?.error || t('ATN_EVOLUTION.ERROR.LOAD'));
  } finally {
    isLoading.value = false;
  }
};

const connect = async instanceName => {
  connectingInstance.value = instanceName;
  selectedInstance.value = instanceName;
  qrCode.value = '';

  try {
    const { data } = await evolutionAPI.connect(instanceName);
    if (data.error) throw new Error(data.message);

    qrCode.value = data.qr_code || '';
    await loadInstances({ silent: true });

    if (!qrCode.value) {
      const instance = instances.value.find(item => item.name === instanceName);
      useAlert(
        String(instance?.status).toLowerCase() === 'open'
          ? t('ATN_EVOLUTION.ALREADY_CONNECTED')
          : t('ATN_EVOLUTION.ERROR.NO_QR')
      );
    }
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        error.message ||
        t('ATN_EVOLUTION.ERROR.CONNECT')
    );
  } finally {
    connectingInstance.value = '';
  }
};

const openDisconnectDialog = instanceName => {
  instanceToDisconnect.value = instanceName;
  disconnectDialog.value?.open();
};

const disconnect = async () => {
  const instanceName = instanceToDisconnect.value;
  disconnectingInstance.value = instanceName;

  try {
    await evolutionAPI.logout(instanceName);
    if (selectedInstance.value === instanceName) {
      selectedInstance.value = '';
      qrCode.value = '';
    }
    disconnectDialog.value?.close();
    await loadInstances({ silent: true });
    useAlert(t('ATN_EVOLUTION.DISCONNECT.SUCCESS'));
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('ATN_EVOLUTION.ERROR.DISCONNECT')
    );
  } finally {
    disconnectingInstance.value = '';
  }
};

onMounted(loadInstances);
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :no-records-found="hasNoInstances"
    :loading-message="t('ATN_EVOLUTION.LOADING')"
    :no-records-message="t('ATN_EVOLUTION.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('ATN_EVOLUTION.TITLE')"
        :description="t('ATN_EVOLUTION.DESCRIPTION')"
      >
        <template #actions>
          <Button
            :label="t('ATN_EVOLUTION.REFRESH')"
            icon="i-lucide-refresh-cw"
            color="slate"
            variant="outline"
            size="sm"
            @click="loadInstances()"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="grid gap-4 mt-3 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <section
          class="overflow-hidden border rounded-xl border-n-weak bg-n-solid-1"
        >
          <div
            v-for="instance in instances"
            :key="instance.name"
            class="flex flex-wrap items-center justify-between gap-4 px-5 py-4 border-b last:border-b-0 border-n-weak"
          >
            <div class="flex items-center min-w-0 gap-3">
              <span
                class="grid flex-shrink-0 rounded-full place-content-center size-9 bg-n-alpha-2"
              >
                <span class="size-4 i-lucide-message-circle text-n-slate-11" />
              </span>
              <div class="min-w-0">
                <p class="mb-1 font-medium truncate text-n-slate-12">
                  {{ instance.name }}
                </p>
                <span
                  class="inline-flex px-2 py-0.5 text-xs font-medium rounded-full"
                  :class="statusClass(instance.status)"
                >
                  {{ statusLabel(instance.status) }}
                </span>
              </div>
            </div>
            <Button
              v-if="String(instance.status).toLowerCase() === 'open'"
              :label="t('ATN_EVOLUTION.DISCONNECT.BUTTON')"
              icon="i-lucide-unplug"
              color="ruby"
              variant="outline"
              size="sm"
              :is-loading="disconnectingInstance === instance.name"
              @click="openDisconnectDialog(instance.name)"
            />
            <Button
              v-else
              :label="t('ATN_EVOLUTION.GENERATE_QR')"
              icon="i-lucide-qr-code"
              color="blue"
              variant="solid"
              size="sm"
              :is-loading="connectingInstance === instance.name"
              @click="connect(instance.name)"
            />
          </div>
        </section>

        <aside
          class="flex flex-col items-center justify-center min-h-80 gap-4 p-6 text-center border border-dashed rounded-xl border-n-weak bg-n-alpha-1"
        >
          <template v-if="qrCode">
            <img
              :src="qrCode"
              :alt="t('ATN_EVOLUTION.QR_ALT', { instance: selectedInstance })"
              class="w-64 max-w-full p-2 bg-white rounded-lg"
            />
            <div>
              <p class="mb-1 font-medium text-n-slate-12">
                {{ selectedInstance }}
              </p>
              <p class="mb-0 text-sm text-n-slate-11">
                {{ t('ATN_EVOLUTION.SCAN_HELP') }}
              </p>
            </div>
            <Button
              :label="t('ATN_EVOLUTION.REFRESH_QR')"
              icon="i-lucide-refresh-cw"
              color="slate"
              variant="outline"
              size="sm"
              :is-loading="connectingInstance === selectedInstance"
              @click="connect(selectedInstance)"
            />
          </template>
          <template v-else>
            <span
              class="grid rounded-full place-content-center size-14 bg-n-alpha-2"
            >
              <span class="size-6 i-lucide-qr-code text-n-slate-11" />
            </span>
            <div>
              <p class="mb-1 font-medium text-n-slate-12">
                {{ t('ATN_EVOLUTION.QR_PLACEHOLDER_TITLE') }}
              </p>
              <p class="mb-0 text-sm text-n-slate-11">
                {{ t('ATN_EVOLUTION.QR_PLACEHOLDER_DESCRIPTION') }}
              </p>
            </div>
          </template>
        </aside>
      </div>
    </template>

    <Dialog
      ref="disconnectDialog"
      type="alert"
      :title="t('ATN_EVOLUTION.DISCONNECT.TITLE')"
      :description="
        t('ATN_EVOLUTION.DISCONNECT.DESCRIPTION', {
          instance: instanceToDisconnect,
        })
      "
      :confirm-button-label="t('ATN_EVOLUTION.DISCONNECT.CONFIRM')"
      :cancel-button-label="t('ATN_EVOLUTION.DISCONNECT.CANCEL')"
      :is-loading="Boolean(disconnectingInstance)"
      @confirm="disconnect"
    />
  </SettingsLayout>
</template>
